/* Template Variables prompt — fires when a starter template declares a
 * `variables` schema (mix-and-match blended templates) OR when a
 * marketplace template's outline contains `{{tokens}}`.
 *
 * Renders a per-variable prompt:
 *   • text          → free-text input (with optional Randomize pool)
 *   • radio         → chip radio-group, single value
 *   • radio + multi → chip CHECKBOX group, array value (interpolated
 *                     as a comma-joined string on apply)
 *
 * Extras:
 *   • Twist Randomizer 🎲 — one-tap fills every field with an
 *     on-genre suggestion.
 *   • `{{token}}` substitution — every occurrence in chapter
 *     `beat_note`/`title` and character `name`/`description` is
 *     replaced with the collected values before `applyStarterTemplate`
 *     runs (or before the marketplace patch loop runs).
 */
import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Check, Sparkles, Dices } from 'lucide-react';
import GenreVariantPicker from './GenreVariantPicker';
import { interp, materialize } from './templateInterp';

// Re-export for existing importers.
export { interp, materialize };

/** Pick a random element from an array. */
function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

/** Return one randomized value for the given variable spec. */
function randomizeVar(v, current) {
  if (v.type === 'radio') {
    const opts = v.options || [];
    if (opts.length === 0) return current;
    if (v.multi) {
      // 1–2 random options, unique.
      const shuffled = [...opts].sort(() => Math.random() - 0.5);
      const take = 1 + (Math.random() < 0.35 ? 1 : 0);
      return shuffled.slice(0, Math.min(take, opts.length));
    }
    return pick(opts);
  }
  // text
  if (Array.isArray(v.random) && v.random.length > 0) return pick(v.random);
  return current; // no pool → leave untouched
}

export default function TemplateVariablesDialog({
  open, onOpenChange, template, busy, onApply, prefill,
}) {
  const initial = useMemo(() => {
    const s = {};
    (template?.variables || []).forEach((v) => {
      // iter55g52 — Series Bible Auto-Link: if a `prefill` value exists
      // for this variable key (sourced from the current project's
      // character bible), use it as the initial value instead of the
      // template's static default.
      const pre = prefill && Object.prototype.hasOwnProperty.call(prefill, v.key)
        ? prefill[v.key] : undefined;
      if (v.type === 'radio' && v.multi) {
        s[v.key] = Array.isArray(pre) ? pre
                   : Array.isArray(v.default) ? v.default
                   : (v.default ? [v.default] : []);
      } else {
        s[v.key] = (pre !== undefined && pre !== null && pre !== '') ? pre : (v.default ?? '');
      }
    });
    return s;
  }, [template, prefill]);

  const [values, setValues] = useState(initial);
  React.useEffect(() => { setValues(initial); }, [initial]);

  // iter55p5 — Genre variants can now be BLENDED. State moved from
  // `genreSlug: string` to `selectedSlugs: string[]` so authors can
  // pick Mafia + Monster and merge their palettes. Later selections
  // override earlier ones on token collision.
  const genreVariants = template?.genre_variants || {};
  const genreSlugs = Object.keys(genreVariants);
  const [selectedSlugs, setSelectedSlugs] = useState(genreSlugs.length ? [genreSlugs[0]] : []);
  React.useEffect(() => {
    setSelectedSlugs(genreSlugs.length ? [genreSlugs[0]] : []);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [template?.id]);

  if (!template) return null;

  const setVal    = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));
  const toggleMulti = (k, opt) => setValues((prev) => {
    const cur = Array.isArray(prev[k]) ? prev[k] : [];
    const next = cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt];
    return { ...prev, [k]: next };
  });

  const rollAll = () => {
    setValues((prev) => {
      const next = { ...prev };
      (template.variables || []).forEach((v) => {
        next[v.key] = randomizeVar(v, prev[v.key]);
      });
      return next;
    });
  };

  const submit = () => {
    // iter55p5 — Merge every selected genre variant in order (later
    // wins on token collision). E.g. selecting Mafia THEN Monster
    // resolves `{{external_threat}}` to Monster's "hunters' guild".
    const chosen = {};
    for (const slug of selectedSlugs) {
      const entry = genreVariants[slug];
      if (!entry) continue;
      Object.assign(chosen, entry.values || entry);
    }
    const merged = { ...values, ...chosen };
    onApply?.(materialize(template, merged), merged);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="template-variables-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <DialogTitle className="text-[16px] inline-flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                Customize: {template.name}
              </DialogTitle>
              <DialogDescription>
                Mix and match. Every choice below is stitched into chapter outlines
                and character stubs before the template is applied. You can edit
                anything afterwards.
                {prefill && Object.keys(prefill).length > 0 && (
                  <span className="mt-1 block text-[10.5px] text-primary" data-testid="tvar-prefill-hint">
                    ✨ Pre-filled from your existing character bible — override any field.
                  </span>
                )}
              </DialogDescription>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={rollAll}
              data-testid="tvar-randomize"
              className="gap-1.5 whitespace-nowrap"
              title="Fill every field with an on-genre suggestion"
            >
              <Dices className="h-3.5 w-3.5" />
              Twist it
            </Button>
          </div>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          <GenreVariantPicker
            template={template}
            values={values}
            selectedSlugs={selectedSlugs}
            setSelectedSlugs={setSelectedSlugs}
          />

          {(template.variables || []).map((v) => (
            <div key={v.key} className="space-y-1.5" data-testid={`tvar-row-${v.key}`}>
              <div className="flex items-center justify-between">
                <Label htmlFor={`tvar-${v.key}`} className="text-[12px] font-medium">
                  {v.label}
                  {v.type === 'radio' && v.multi && (
                    <span className="ml-1.5 text-[10px] text-muted-foreground">
                      (pick one or more)
                    </span>
                  )}
                </Label>
                {v.type === 'text' && Array.isArray(v.random) && v.random.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setVal(v.key, pick(v.random))}
                    data-testid={`tvar-reroll-${v.key}`}
                    className="text-[10px] uppercase tracking-wide text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                    title="Reroll this field"
                  >
                    <Dices className="h-3 w-3" /> reroll
                  </button>
                )}
              </div>

              {v.type === 'radio' ? (
                <div className="flex flex-wrap gap-2" role={v.multi ? 'group' : 'radiogroup'}>
                  {v.options.map((opt) => {
                    const selected = v.multi
                      ? (Array.isArray(values[v.key]) && values[v.key].includes(opt))
                      : values[v.key] === opt;
                    return (
                      <button
                        type="button"
                        key={opt}
                        onClick={() => (v.multi ? toggleMulti(v.key, opt) : setVal(v.key, opt))}
                        data-testid={`tvar-${v.key}-${opt.replace(/\s+/g, '-').toLowerCase()}`}
                        className={
                          'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11.5px] cursor-pointer transition ' +
                          (selected
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border bg-card hover:border-primary/40')
                        }
                      >
                        {selected && <Check className="h-3 w-3" />}
                        {opt}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <Input
                  id={`tvar-${v.key}`}
                  data-testid={`tvar-input-${v.key}`}
                  value={values[v.key] || ''}
                  placeholder={v.placeholder || ''}
                  onChange={(e) => setVal(v.key, e.target.value)}
                  className="h-8 text-[12.5px]"
                />
              )}
            </div>
          ))}
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            data-testid="tvar-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!!busy}
            data-testid="tvar-apply"
            className="gap-1.5"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Apply template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
